/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useRef, useState } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';
import { putWithFile, uploadSingleLogo } from './utils';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFilePoster from 'filepond-plugin-file-poster';

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImageResize,
  FilePondPluginImageValidateSize,
  FilePondPluginFileValidateType,
  FilePondPluginFilePoster
);

interface FilePondUploaderProps {
  name?: string;
  labelIdle?: string;
  allowMultiple?: boolean;
  maxFiles?: number;
  onFilesUpdate?: any;
  defaultFiles: any[];
  shape: string;
}

export const FilePondUploader: React.FC<FilePondUploaderProps> = ({
  name = 'files',
  labelIdle = 'Ziehe und lege deine Datei ab oder <span class="filepond--label-action">wähle aus</span>',
  allowMultiple = false,
  maxFiles = 1,
  defaultFiles,
  onFilesUpdate,
  shape
}) => {
  const validateImageDimensions = async (file: File) => {
    if (!shape) return true;

    return await new Promise<boolean>((resolve) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);

        const isValid =
          shape === 'square'
            ? image.naturalWidth === image.naturalHeight
            : image.naturalWidth !== image.naturalHeight;

        resolve(isValid);
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(false);
      };

      image.src = objectUrl;
    });
  };

  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const locationDetails = useSelector((state: any) => state.locationDetails);
  const { t } = useTranslate();
  const pondContainer = useRef<HTMLDivElement>(null);

  const handleUpdateFiles = async (fileItems: any[], locationUUID: string) => {
    setUploadedFiles(fileItems);

    if (fileItems.length > 0) {
      const rawFile = fileItems[0].file;
      try {
        const result = await uploadSingleLogo({
          file: rawFile,
          locationUuid: locationUUID,
          t,
          toast
        });

        const res = await putWithFile(
          rawFile,
          result.uploadPreset,
          () => {
            console.log('Upload Success!');
          },
          () => {
            console.log('Upload Error!');
          }
        );

        onFilesUpdate?.(result);
      } catch (error) {
        console.error('Error in uploadSingleLogo:', error);
      }
    }
  };

  if (!locationDetails.uuid) return <div>loading</div>;

  return (
    <div>
      {/* Preview Card */}
      <ImagePreview
        defaultFiles={defaultFiles}
        uploadedFiles={uploadedFiles}
        onFilesUpdate={() => {
          onFilesUpdate(null);
          setUploadedFiles([]);
        }}
      />

      {/* FilePond Uploader */}
      {uploadedFiles?.length === 0 && defaultFiles?.length === 0 && (
        <div ref={pondContainer}>
          <FilePond
            beforeAddFile={async (file) => {
              const isValid = await validateImageDimensions(file.file as File);

              if (!isValid) {
                toast.error(t('lomavisBranding.error.invalid_dimensions'));
                return false;
              }

              return true;
            }}
            server={null}
            credits={false}
            acceptedFileTypes={[
              'image/png',
              'image/jpeg',
              'image/heic',
              'image/heif',
              'image/heic-sequence',
              'image/heif-sequence'
            ]}
            stylePanelLayout="compact"
            allowMultiple={allowMultiple}
            maxFiles={maxFiles}
            onremovefile={() => {
              setUploadedFiles([]);
              onFilesUpdate?.(null);
            }}
            name={name}
            labelIdle={labelIdle}
            onaddfile={async (error, fileItem) => {
              if (!error && fileItem) {
                await handleUpdateFiles([fileItem], locationDetails?.uuid);
              }
            }}
            // files={uploadedFiles}
          />
        </div>
      )}
    </div>
  );
};

const ImagePreview = ({ defaultFiles, uploadedFiles, onFilesUpdate }: any) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const { t } = useTranslate();
  useEffect(() => {
    let objectUrl: string | null = null;
    if (uploadedFiles?.length > 0) {
      // create a a url for the image:
      try {
        objectUrl = URL.createObjectURL(uploadedFiles[0].file);
      } catch (error) {
        toast.error(
          t('lomavis.settings.branding.template_branding.upload_preview_error'),
          { position: 'top-right' }
        );
      }
      setImagePreview(objectUrl);
    } else if (defaultFiles?.length > 0) {
      console.log('was this executed!');
      setImagePreview(defaultFiles[0].url);
    } else {
      setImagePreview(null);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [defaultFiles, uploadedFiles]);

  if (!imagePreview) return null;

  return (
    <div
      className="mb-4 h-100px"
    >
      {/* Delete Button */}
      <div
        className="position-absolute mb-n5 me-n5 badge badge-secondary cursor-pointer"
        onClick={() => onFilesUpdate(null)}
      >
        <i className="fas fa-times text-white"></i>{' '}
        {/* FontAwesome Times Icon */}
      </div>

      {/* Image */}
      <img
        src={imagePreview}
        alt="Preview"
        className="img-thumbnail h-100px rounded-2"
        style={{ objectFit: 'cover' }} // Ensures the image covers the container without distortion
      />
    </div>
  );
};
